<template>
  <input
    type="text"
    :value="displayValue"
    @input="onInput"
    @blur="onBlur"
    @focus="onFocus"
    @keypress="onKeypress"
    class="sys-input-number"
    :class="{ 'text-center': center }"
    :disabled="disabled"
  />
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  value: { type: [Number, String], default: null },
  max: { type: Number, default: 99999999 },
  decimals: { type: Number, default: 4 },
  center: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['input']);

const displayValue = ref('');
const isFocused = ref(false);

const format = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const num = parseFloat(val);
  if (isNaN(num)) return '';

  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: props.decimals,
    useGrouping: false
  });
};

watch(() => props.value, (newVal) => {
  if (!isFocused.value) {
    displayValue.value = format(newVal);
  }
}, { immediate: true });

const onFocus = (e) => {
  isFocused.value = true;
  e.target.select();
};

const onInput = (e) => {
  let val = e.target.value;
  displayValue.value = val;

  let numStr = val.replace(',', '.');
  let num = parseFloat(numStr);

  if (!isNaN(num)) {
    if (num > props.max) {
       num = props.max;
    }
    emit('input', num);
  } else {
    if (val === '') emit('input', null);
  }
};

const onBlur = () => {
  isFocused.value = false;
  let val = displayValue.value.replace(',', '.');

  if (val === '') {
    emit('input', null);
    displayValue.value = '';
    return;
  }

  let num = parseFloat(val);
  if (isNaN(num)) {
    displayValue.value = format(props.value);
    return;
  }

  if (num > props.max) num = props.max;
  if (num < 0) num = 0;

  const factor = Math.pow(10, props.decimals);
  num = Math.round(num * factor) / factor;

  emit('input', num);
  displayValue.value = format(num);
};

const onKeypress = (e) => {
  const char = String.fromCharCode(e.keyCode);
  if (!/[0-9,]/.test(char)) {
    e.preventDefault();
    return;
  }
  
  if (char === ',' && displayValue.value.includes(',')) {
    e.preventDefault();
    return;
  }

  if (char !== ',') {
    const maxDigits = Math.floor(props.max).toString().length;
    const parts = displayValue.value.split(',');
    const integerPart = parts[0];
    
    const input = e.target;
    const cursorPosition = input.selectionStart;
    const commaIndex = displayValue.value.indexOf(',');
    const isEditingIntegerPart = commaIndex === -1 || cursorPosition <= commaIndex;

    if (isEditingIntegerPart && integerPart.length >= maxDigits) {
      if (input.selectionStart === input.selectionEnd) {
        e.preventDefault();
      }
    }
  }
};
</script>

<style scoped>
.sys-input-number {
  width: 100%;
  outline: none;
  border: 1px solid transparent;
  border-bottom: 1px solid #9e9e9e;
  background: transparent;
  padding: 0;
  margin: 0;
  font-size: inherit;
  color: inherit;
}
.sys-input-number:focus {
  border-bottom: 1px solid #1976d2;
}
.text-center {
  text-align: center;
}
</style>
